/**
 * Lot CYC (V650 ; registre n° 13) — cycle de vie des projets et des organisations.
 *
 * - Projet : renommer, archiver (confirmé), désarchiver — propriétaire du projet ou administration de
 *   l'organisation ; supprimer — administration de l'organisation.
 * - Organisation : supprimer — administration de la plateforme (jamais la sienne).
 * - Toute suppression : export complet d'abord (archive téléchargeable, empreinte SHA-256), puis purge
 *   DIFFÉRÉE (30 jours par défaut), annulable pendant le délai ; l'objet est en lecture seule et signalé.
 */
import type { Session } from '../auth/session'
import { estAdministrateurOrganisation, getToken } from '../auth/session'
import { ApiError, BASE, httpMessage, request } from './http'

const enc = encodeURIComponent

/**
 * Miroir CÔTÉ INTERFACE du prédicat serveur `GovernanceAccess.administrateurOrganisation()`
 * (administration de la plateforme ou rôle d'administration de l'organisation). Il décide de ce qui
 * est PROPOSÉ ; la passerelle reste seule juge (refus nommés, affichés tels quels).
 */
export function estAdministrationOrganisation(session: Session | null | undefined): boolean {
  // Intégration CYC × USR : le serveur n'accepte plus le rôle de realm `workspace_owner`
  // (ARB-SEC-USR-01) — plateforme ou administrateur d'organisation (`org_admin`) seulement.
  return Boolean(session?.roles.includes('admin')) || estAdministrateurOrganisation(session ?? null)
}

export interface ReglagesCycle {
  delay_days_default: number
  delay_days_min: number
  delay_days_max: number
}

export interface ProjetAdministre {
  workspace_id: string
  name: string
  description: string | null
  status: 'active' | 'archived' | string
  program_id: string | null
  program_name: string | null
  launch_status: string | null
  created_at: string | null
  updated_at: string | null
  members: number
  owners: string | null
  membership_role: string | null
  deletion_request_id: number | null
  purge_after: string | null
  export_state: string | null
}

export type EtatDemande = 'scheduled' | 'cancelled' | 'purged' | 'superseded'
export type EtatExport = 'pending' | 'ready' | 'failed' | 'expired'

export interface LignePerimetre {
  table_name: string
  mode: 'purge' | 'conserve' | 'pierre_tombale'
  raison: string
  lignes: number | null
}

export interface RapportPurge {
  lignes_purgees: number
  tables: Record<string, number>
  passes: number
  conserve: { table: string; raison: string }[]
  duree_ms: number
  export_sha256: string
}

export interface DemandeSuppression {
  id: number
  scope: 'project' | 'organisation'
  organisation_id: string
  workspace_id: string | null
  target_name: string
  reason: string
  delay_days: number
  requested_by: string
  /** Nom affiché de qui a demandé (N-27), cherché dans l'organisation de la demande : `null` pour qui n'en est pas
   *  un utilisateur (la plateforme qui supprime une organisation) ; absent d'une passerelle antérieure — l'écran
   *  retombe alors sur l'identifiant, sans nom fabriqué. */
  requested_by_name?: string | null
  requested_at: string
  purge_after: string
  previous_status: string
  state: EtatDemande
  export_state: EtatExport
  export_attempts: number
  export_sha256: string | null
  export_size_bytes: number | null
  export_ready_at: string | null
  export_error: string | null
  export_retained_until: string | null
  cancelled_by: string | null
  /** Nom affiché de qui a annulé (N-27), mêmes règles que `requested_by_name`. */
  cancelled_by_name?: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  purge_attempts: number
  purge_error: string | null
  purged_at: string | null
  purge_report: RapportPurge | null
  superseded_by: number | null
  can_cancel?: boolean
  can_download?: boolean
  perimeter?: LignePerimetre[]
}

export interface EtatCycleOrganisation {
  organisation: { id: number; purge_after: string; export_state: string } | null
  projects: { id: number; workspace_id: string; name: string; purge_after: string }[]
}

export interface CorpsSuppression {
  reason: string
  delay_days: number
  confirmation: string
}

/** Clés de cache. */
export const CLE_PROJETS_ADMIN = ['organisation', 'projets', 'administration'] as const
export const CLE_ETAT_CYCLE = ['organisation', 'cycle-de-vie'] as const
export const cleDemande = (id: number | string): readonly unknown[] => ['suppressions', String(id)]

export const cycleDeVieApi = {
  projetsOrganisation: () =>
    request<{ projects: ProjetAdministre[]; settings: ReglagesCycle }>('/organisation/projets'),
  renommer: (workspaceId: string, name: string, description: string | null) =>
    request<ProjetAdministre>(`/workspaces/${enc(workspaceId)}`, {
      method: 'PATCH', body: JSON.stringify({ name, description }),
    }),
  archiver: (workspaceId: string) =>
    request<unknown>(`/workspaces/${enc(workspaceId)}/archive`, { method: 'PATCH' }),
  desarchiver: (workspaceId: string) =>
    request<unknown>(`/workspaces/${enc(workspaceId)}/unarchive`, { method: 'POST' }),
  supprimerProjet: (workspaceId: string, corps: CorpsSuppression) =>
    request<DemandeSuppression>(`/workspaces/${enc(workspaceId)}/suppression`, {
      method: 'POST', body: JSON.stringify(corps),
    }),
  supprimerOrganisation: (organisationId: string, corps: CorpsSuppression) =>
    request<DemandeSuppression>(`/admin/organisations/${enc(organisationId)}/suppression`, {
      method: 'POST', body: JSON.stringify(corps),
    }),
  demandesOrganisation: () =>
    request<{ requests: DemandeSuppression[]; settings: ReglagesCycle }>('/organisation/suppressions'),
  demandesPlateforme: () =>
    request<{ requests: DemandeSuppression[]; settings: ReglagesCycle }>('/admin/suppressions'),
  demande: (id: number | string) => request<DemandeSuppression>(`/suppressions/${enc(String(id))}`),
  annuler: (id: number | string, reason: string) =>
    request<DemandeSuppression>(`/suppressions/${enc(String(id))}/annulation`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),
  etat: () => request<EtatCycleOrganisation>('/organisation/cycle-de-vie'),
  /** Télécharge l'archive d'export avec le jeton de session ; rend l'empreinte annoncée. */
  telechargerExport: async (id: number | string): Promise<string | null> => {
    const token = getToken()
    const reponse = await fetch(`${BASE}/suppressions/${enc(String(id))}/export`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    if (!reponse.ok) {
      throw new ApiError(reponse.status, httpMessage(reponse.status), await reponse.text().catch(() => undefined))
    }
    const blob = await reponse.blob()
    const url = URL.createObjectURL(blob)
    const lien = document.createElement('a')
    lien.href = url
    lien.download = `doit-suppression-${id}.zip`
    document.body.appendChild(lien)
    lien.click()
    lien.remove()
    URL.revokeObjectURL(url)
    return reponse.headers.get('X-Export-Sha256')
  },
}

/** Taille lisible d'une archive. */
export function tailleLisible(octets: number | null | undefined): string {
  if (octets == null) return '—'
  if (octets < 1024) return `${octets} o`
  if (octets < 1024 * 1024) return `${(octets / 1024).toFixed(1)} Kio`
  return `${(octets / (1024 * 1024)).toFixed(1)} Mio`
}
